import { Component, input, OnDestroy, OnInit } from '@angular/core';

import { UntypedFormBuilder } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';

import {
  ConfigurationService,
  QueryTopicItem,
} from 'app/providers/semantria-api/configuration.service';

import { EvaluationService } from 'app/providers/semantria-api/evaluation-service';
import { DocumentAnalysis } from 'app/providers/storage-api/analysis.service';
import { ConfigurationTesterService } from 'app/providers/storage-api/configurations.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';

import { ConfigurationDetailsTabBase, Highlight } from '../configuration-details-tab-base';
import { ConfigurationQueryTopicsPurposeDialogComponent } from '../configuration-query-topics-purpose-dialog/configuration-query-topics-purpose-dialog.component';
import { ConfigurationVersionTrackerService } from '../../services/configuration-version-tracker.service';
import { QueryTopicService } from '../../services/query-topic.service';
import { TaxonomyTargetWatcherService } from '../../services/taxonomy-target-watcher.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-query-topics',
  templateUrl: './configuration-query-topics.component.html',
  styleUrls: ['./configuration-query-topics.component.scss'],
  standalone: false,
})
export class ConfigurationQueryTopicsComponent
  extends ConfigurationDetailsTabBase<QueryTopicItem>
  implements OnInit, OnDestroy
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly LONG_PURPOSE_PROPERTY_NAME = 'LONG-PURPOSE';
  readonly APPLY_TO_SECTIONS_SEPARATOR = ',';

  tabIsActive = input(false);

  editingTopic: QueryTopicItem;

  originalHidden: boolean;
  originalLongPurpose = '';
  originalApplyToSections = '';

  isSaving: boolean;

  hasPurposeGenerationPermission = false;

  getHighlightText = (
    doc: DocumentAnalysis,
    documentText: string,
    itemValues: string[],
    lookInSections: boolean = false,
    sectionId?: string
  ) => {
    const highlights: Highlight[] = [];

    if (doc?.topics) {
      doc.topics
        .filter(
          (topic) => topic?.type === 'query' && topic?.mentions && itemValues?.includes(topic.title)
        )
        .reduce((prev, topic) => prev.concat(topic.mentions || []), [])
        .filter((mention) => mention?.locations)
        .forEach((mention) => {
          mention.locations.forEach((location) => {
            if (!location) {
              return;
            }

            // If we are looking at a specific section, ensure the location matches that section
            // sectionId is a string and location.section is an int, so we convert to string for comparison
            const locSection = (location as any)?.section;
            if (sectionId && (locSection == null || String(locSection) !== sectionId)) {
              return;
            }

            let offset, length;

            if (lookInSections) {
              // Use section-level offsets
              if (location.hasOwnProperty('section_char_offset')) {
                offset = location.section_char_offset;
              } else if (location.hasOwnProperty('char_offset')) {
                offset = location.char_offset;
              } else {
                offset = location.offset;
              }

              if (location.hasOwnProperty('section_char_length')) {
                length = location.section_char_length;
              } else if (location.hasOwnProperty('char_length')) {
                length = location.char_length;
              } else {
                length = location.length;
              }
            } else {
              // Use document-level offsets
              offset = location.hasOwnProperty('char_offset')
                ? location.char_offset
                : location.offset;
              length = location.hasOwnProperty('char_length')
                ? location.char_length
                : location.length;
            }

            const offsetType =
              location.hasOwnProperty('char_offset') ||
              location.hasOwnProperty('section_char_offset')
                ? 'char'
                : 'byte';

            // Validate highlight is within bounds and matches text (if label available)
            if (offset >= 0 && offset + length <= documentText.length) {
              const textMatch =
                !mention.label || documentText.substring(offset, offset + length) === mention.label;

              if (textMatch) {
                highlights.push({ offset, length, offsetType });
              }
            }
          });
        });
    }

    this.setDocumentHitIcon(!!highlights.length, doc.id);

    return super.highlightTextStringFromHighlights(highlights, documentText);
  };

  private onDestroy$ = new Subject<void>();

  constructor(
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected configVersionTracker: ConfigurationVersionTrackerService,
    private dialogService: DialogService,
    private configService: ConfigurationService,
    private evaluationService: EvaluationService,
    private targetWatcher: TaxonomyTargetWatcherService,
    private queryTopicService: QueryTopicService,
    private configurationTesterService: ConfigurationTesterService
  ) {
    super(route, configVersionTracker);

    this.itemType = 'Query Topic';

    this.loadItems = this.configService.getQueryTopics.bind(this.configService);
    this.createItem = this.configService.createQueryTopic.bind(this.configService);
    this.saveItem = this.configService.saveQueryTopic.bind(this.configService);
    this.deleteItem = this.configService.deleteQueryTopic.bind(this.configService);
    this.deleteAllItems = this.configService.deleteAllQueryTopics.bind(this.configService);
    this.evaluateItem = this.evaluationService.evaluateDocuments.bind(this.evaluationService);
    this.evaluateDataset = this.configurationTesterService.evaluate.bind(
      this.configurationTesterService
    );
    this.getEvaluationConfig = this.configService.getEvaluationConfigId.bind(this.configService);
    this.getItemByClonedFrom = this.configService.getQueryTopicByClonedFrom.bind(
      this.configService
    );
    this.deleteEvaluationConfig = this.configService.deleteConfiguration.bind(this.configService);
  }

  ngOnInit(): void {
    this.queryTopicService
      .hasPurposeGenerationPermission()
      .then((hasPermission) => (this.hasPurposeGenerationPermission = hasPermission));
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  /**
   * @fixme
   */
  setDocumentHitIcon(hit, docId, hitScore?) {
    const checkMarkItem = document.getElementById('evaluation-checkmark-' + docId);
    if (checkMarkItem) {
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--hit');
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--miss');
      checkMarkItem.title = '';
      if (hit) {
        checkMarkItem.classList.add('configuration-item-editor__evaluation-checkmark--hit');
        if (hitScore) {
          checkMarkItem.title = translate('configuration-query-topics-component.score-title', {
            hitScore,
          });
        }
      } else {
        checkMarkItem.classList.add('configuration-item-editor__evaluation-checkmark--miss');
        checkMarkItem.title = translate('configuration-query-topics-component.did-not-hit', {
          itemType: this.itemType,
        });
      }
    }
  }

  onItemEditing($event: QueryTopicItem) {
    this.editingTopic = $event;

    if ($event) {
      this.originalHidden = $event.hidden;
      this.originalLongPurpose = $event.properties?.[this.LONG_PURPOSE_PROPERTY_NAME];
      this.originalApplyToSections = ($event.channels ?? []).join(this.APPLY_TO_SECTIONS_SEPARATOR);
    }
  }

  onItemSaving($event) {
    this.isSaving = $event;
  }

  getItemHighlightValue(item) {
    return [item.name];
  }

  onHiddenChanged(value: boolean, item) {
    item.hidden = value;
    this.refreshEditorTemplateHasChanges();
  }

  onApplyToSectionsChanged(value: string, item: QueryTopicItem) {
    item.channels = (value ?? '')
      .split(this.APPLY_TO_SECTIONS_SEPARATOR)
      .map((s) => s.trim())
      .filter(Boolean);
    this.refreshEditorTemplateHasChanges();
  }

  onPurposeChanges(value: string, item: QueryTopicItem) {
    if (!item.properties) {
      item.properties = {};
    }

    item.properties[this.LONG_PURPOSE_PROPERTY_NAME] = value;
    this.refreshEditorTemplateHasChanges();
  }

  refreshEditorTemplateHasChanges(): void {
    // FIXME: It's legacy code that was moved to a separate method. We shouldn't modify component attributes that are marked as @Input(),
    // but the type of this.editor.editorTemplateHasChanges will be changed to model (writeable signal) in DASH-9703, so I'll leave this for now.
    this.editor.editorTemplateHasChanges.set(
      this.editingTopic.hidden !== !!this.originalHidden ||
        this.editingTopic.properties?.[this.LONG_PURPOSE_PROPERTY_NAME] !==
          this.originalLongPurpose ||
        (this.editingTopic.channels ?? []).join(this.APPLY_TO_SECTIONS_SEPARATOR) !==
          this.originalApplyToSections
    );
  }

  onItemAdded(topic) {
    super.onItemAdded(topic);
    this.targetWatcher.queryTopicAdded(topic);
  }

  onItemUpdated(topic) {
    super.onItemUpdated(topic);
    this.targetWatcher.queryTopicModified(topic);
  }

  onItemsDeleted(topics) {
    super.onItemsDeleted(topics);
    this.targetWatcher.queryTopicsDeleted(topics);
  }

  onAllItemsDeleted($event) {
    super.onAllItemsDeleted($event);
    this.targetWatcher.allQueryTopicsDeleted($event);
  }

  onEditQueryTopicPurpose() {
    this.openPurposeDialog(false);
  }

  onGenerateQueryTopicPurpose() {
    this.openPurposeDialog(true);
  }

  private openPurposeDialog(generateWithGPT: boolean) {
    const dialogRef = this.dialogService.showDialog(
      ConfigurationQueryTopicsPurposeDialogComponent,
      {
        longPurpose: this.editingTopic.properties?.[this.LONG_PURPOSE_PROPERTY_NAME],
        query: this.editingTopic.query,
        generateWithGPT,
      },
      {},
      false
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result != null) {
          this.onPurposeChanges(result, this.editingTopic);
        }
      });
  }
}
